import { ErrorDisplay } from '@/components/error';
import { LoadingIndicator } from '@/components/loading-indicator';
import { TableBuilder } from '@tigergraph/app-ui-lib/table';
import { TableBuilderColumn } from 'baseui/table-semantic';
import { WorkGroupT, WorkspaceT, isStatusActive } from '@/pages/workgroup/type';
import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { useState, useEffect, useRef } from 'react';
import { useTheme } from '@/contexts/themeContext';
import { WorkspaceSelector } from '@/pages/editor/WorkspaceSelector';
import { useQuery } from 'react-query';
import { fetchGraphList } from '@/lib/instance_api';
import { ID_TOKEN_KEY, useWorkspaceContext } from '@/contexts/workspaceContext';
import { GraphSelector } from '@/pages/explore/explore/GraphSelector';
import { StatisticsType, useQueryGraphStatistics } from '@/pages/dashboard/hook';
import { Chart } from '@tigergraph/app-ui-lib/charts';
import NoGraph from '@/pages/dashboard/NoGraph';
import EmptyState from '@/pages/workgroup/EmptyState';
import { useSearchParams } from 'react-router-dom';
import mediaQuery from '@/lib/breakpoints';
import { ECharts } from 'echarts';
import { useMobile } from '@/contexts/mobileContext';

type Props = {
  group: WorkGroupT;
};

export default function QuickInsightLists({ group }: Props) {
  const [css] = useStyletron();
  const [searchParams] = useSearchParams();
  const { my_ip } = useWorkspaceContext();

  const { workspaces } = group;
  // initial workspace with active workspace
  const [workspace, setWorkspace] = useState<WorkspaceT | undefined>(
    searchParams.get('workspace_id')
      ? workspaces.filter((w) => w.workspace_id === searchParams.get('workspace_id'))[0]
      : workspaces.filter((w) => isStatusActive(w.status))[0]
  );
  const [currentGraph, setCurrentGraph] = useState<string>(searchParams.get('graph_name') || '');

  const { data: graphs = [], isFetching: isFetchingGraph } = useQuery(
    ['graph_list', workspace?.nginx_host],
    async () => {
      if (!workspace?.nginx_host) {
        return;
      }
      return fetchGraphList(workspace?.nginx_host, workspace?.tg_version, sessionStorage.getItem(ID_TOKEN_KEY)!);
    },
    {
      enabled: workspace?.nginx_host !== undefined,
      onSuccess(data) {
        // setup default graph name
        if (data && data.length > 0) {
          // check if currentGraph is in the list
          if (!data.includes(currentGraph)) {
            const nonDefaultGraphs = data.filter((d) => d !== 'MyGraph');
            setCurrentGraph(nonDefaultGraphs.length > 0 ? nonDefaultGraphs[0] : data[0]);
          }
        }
      },
    }
  );

  let { data, isLoading, isError, error } = useQueryGraphStatistics(workspace?.nginx_host || '', currentGraph, {
    enabled: workspace?.nginx_host !== undefined && currentGraph !== '',
  });

  if (data && (data.vertexCounts.error || data.edgeCounts.error)) {
    isError = true;
    // @ts-ignore
    error = new Error(data.edge.error || data.vertex.error);
  }

  if (!group.is_current_ip_allowed) {
    return <ErrorDisplay error={new Error(`Your IP ${my_ip} is not allowed to access workspace data in the group.`)} />;
  }

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        height: '100%',
      })}
    >
      <div
        className={css({
          display: 'flex',
          justifyContent: 'flex-end',
          [mediaQuery.xsmall]: {
            flexDirection: 'column',
            gap: '8px',
          },
        })}
      >
        <WorkspaceSelector
          isLocalSelector={true}
          workspace={workspace}
          onWorkspaceChange={setWorkspace}
          workgroupId={group.workgroup_id}
        />
        <div
          className={css({
            width: '200px',
            [mediaQuery.xsmall]: {
              width: '100%',
            },
          })}
        >
          <GraphSelector
            graphs={graphs}
            graph={currentGraph}
            onGraphChanged={(graphName) => {
              setCurrentGraph(graphName);
            }}
            isFetching={isFetchingGraph}
          />
        </div>
      </div>
      {(isLoading || isFetchingGraph) && <LoadingIndicator />}
      {isError && <ErrorDisplay error={error} />}
      {workspaces.length === 0 || !workspace ? (
        <div className={css({ flexBasis: 0, flexGrow: 1 })}>
          <EmptyState group={group} toPage="spaces/config" />
        </div>
      ) : (
        !isFetchingGraph && !currentGraph && workspace && <NoGraph space_id={workspace.workspace_id} />
      )}
      {!isLoading && !isError && data && <Dashboard data={data} />}
    </div>
  );
}

function Dashboard({ data }: { data: StatisticsType }) {
  const [css] = useStyletron();
  const { vertexCounts, edgeCounts } = data;
  const totalVertices = vertexCounts.results ? vertexCounts.results.reduce((acc, cur) => acc + cur.count, 0) : 0;
  const totalEdges = edgeCounts.results ? edgeCounts.results.reduce((acc, cur) => acc + cur.count, 0) : 0;

  return (
    <div
      className={css({
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '16px',
        paddingBottom: '16px',
        [mediaQuery.small]: {
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        },
      })}
    >
      <TotalWidget totalVertices={totalVertices} totalEdges={totalEdges} />
      <CountDistributeByType
        label="Vertex Distribution"
        counts={(vertexCounts.results || []).map((v) => ({
          type: v.v_type,
          count: v.count,
        }))}
      />
      <CountByType
        label="Vertex Type Count"
        counts={(vertexCounts.results || []).map((v) => ({
          type: v.v_type,
          count: v.count,
        }))}
      />
      <CountDistributeByType
        label="Edge Distribution"
        counts={(edgeCounts.results || []).map((v) => ({
          type: v.e_type,
          count: v.count,
        }))}
      />
      <CountByType
        label="Edge Type Count"
        counts={(edgeCounts.results || []).map((v) => ({
          type: v.e_type,
          count: v.count,
        }))}
      />
    </div>
  );
}

function TotalWidget({ totalEdges, totalVertices }: { totalVertices: number; totalEdges: number }) {
  const [css, theme] = useStyletron();
  return (
    <div
      className={css({
        padding: '16px 32px 0px',
        borderRadius: '2px',
        border: `1px solid ${theme.colors['border.tertiary']}`,
        display: 'flex',
        backgroundColor: theme.colors['background.primary'],
        gridColumn: 'span 2',
        [mediaQuery.small]: {
          padding: '16px 16px 0px',
        },
      })}
    >
      <TotalItem label="Number of Vertices" count={totalVertices} />
      <TotalItem label="Number of Edges" count={totalEdges} />
    </div>
  );
}

function TotalItem({ label, count }: { label: string; count: number }) {
  const [css, theme] = useStyletron();
  return (
    <div className={css({ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 })}>
      <span
        className={css({
          fontSize: '18px',
          color: theme.colors['text.secondary'],
          [mediaQuery.small]: {
            fontSize: '16px',
          },
        })}
      >
        {label}
      </span>
      <span
        className={css({
          fontSize: '90px',
          fontWeight: 400,
          [mediaQuery.small]: {
            fontSize: '60px',
          },
          [mediaQuery.xsmall]: {
            fontSize: '40px',
          },
        })}
      >
        {new Intl.NumberFormat().format(count)}
      </span>
    </div>
  );
}

function CountDistributeByType({ label, counts }: { label: string; counts: { type: string; count: number }[] }) {
  const { themeType } = useTheme();
  const [css, theme] = useStyletron();
  const sortedCounts = counts.sort((a, b) => b.count - a.count);
  const chartRef = useRef<ECharts | null>(null);
  const { isMobile } = useMobile();

  const option = {
    title: {
      text: label,
      textStyle: {
        fontSize: 16,
      },
    },
    tooltip: {
      trigger: 'item',
    },
    series: [
      {
        data: sortedCounts.map((c) => ({
          value: c.count,
          name: c.type,
        })),
        type: 'pie',
      },
    ],
  };

  useEffect(() => {
    const handleResize = () => {
      if (chartRef.current) {
        const parentElement = chartRef.current.getDom().parentElement?.parentElement;
        if (parentElement) {
          const width = parentElement.clientWidth;
          chartRef.current.resize({
            width: isMobile ? width : width / 2 - 16,
          });
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isMobile]);

  return (
    <div
      className={css({
        borderRadius: '2px',
        border: `1px solid ${theme.colors['border.tertiary']}`,
        display: 'flex',
        backgroundColor: theme.colors['background.primary'],
        height: '400px',
      })}
    >
      <Chart
        ref={(instance) => {
          if (instance) {
            chartRef.current = instance.getEchartsInstance();
          }
        }}
        option={option}
        style={{ height: '100%', width: '100%', padding: '18px  12px' }}
        theme={themeType === 'dark' ? themeType : undefined}
      />
    </div>
  );
}

function CountByType({ label, counts }: { label: string; counts: { type: string; count: number }[] }) {
  const [css, theme] = useStyletron();
  const sortedCounts = counts.sort((a, b) => b.count - a.count);

  let format = new Intl.NumberFormat();

  return (
    <div
      className={css({
        borderRadius: '2px',
        border: `1px solid ${theme.colors['border.tertiary']}`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        backgroundColor: theme.colors['background.primary'],
        height: '400px',
        padding: '16px',
      })}
    >
      <div
        className={css({
          fontWeight: 'bold',
          fontSize: '18px',
        })}
      >
        {label}
      </div>
      <TableBuilder data={sortedCounts} size="compact">
        <TableBuilderColumn header="Type" id="type">
          {(data) => data.type}
        </TableBuilderColumn>
        <TableBuilderColumn header="Count" id="count">
          {(data) => format.format(data.count)}
        </TableBuilderColumn>
      </TableBuilder>
    </div>
  );
}
